"use client";

import React from "react";
import { Box, Camera, ChevronRight, Droplets, Gamepad2, Plus, Scissors } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
const categories = [{
  id: "optical-fiber",
  title: "Optical Fiber Solutions",
  description: "High-purity G652D and G657A2 & G657A1 series fibers engineered for ultra-low attenuation in AI data centers and global telecom networks. Large Stock Available.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604101/img/advanced_optical_fiber_solution.png",
  icon: "droplets"
}, {
  id: "drone-propulsion",
  title: "Drone Propulsion Systems",
  description: "High-torque brushless motors and precision propellers designed for maximum thrust-to-weight efficiency in FPV racing.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604101/img/drone_propulsion_system_render.png",
  icon: "box"
}, {
  id: "ai-infrastructure",
  title: "AI Data Center Connectivity",
  description: "High-density interconnect systems designed for massive bandwidth and minimal latency in next-generation computing environments.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604101/img/smart_pet_toy_render.png",
  icon: "gamepad2"
}, {
  id: "fpv-systems",
  title: "FPV Video Transmission",
  description: "Low-latency video transmitters and high-definition cameras for real-time aerial intelligence and professional racing.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604101/img/pet_camera_render.png",
  icon: "camera"
}, {
  id: "flight-control",
  title: "Flight Control Electronics",
  description: "Advanced FCs and ESCs featuring precision IMUs and high-speed processing for stable, responsive drone maneuvers.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604101/img/nexel_flight_control_system.png",
  icon: "scissors"
}];
const hotspots = [{
  categoryId: "optical-fiber",
  x: 30,
  y: 25,
  label: "Core Purity",
  spec: "G.657.A2 Bend-Insensitive Core"
}, {
  categoryId: "optical-fiber",
  x: 75,
  y: 45,
  label: "Attenuation",
  spec: "≤ 0.18 dB/km @ 1550nm"
}, {
  categoryId: "optical-fiber",
  x: 50,
  y: 50,
  label: "Coating",
  spec: "Dual-layer UV Acrylate Protection"
}, {
  categoryId: "drone-propulsion",
  x: 45,
  y: 35,
  label: "Thrust",
  spec: "2.5kg+ Peak Thrust Output"
}, {
  categoryId: "drone-propulsion",
  x: 65,
  y: 75,
  label: "Efficiency",
  spec: "92% Peak Motor Efficiency"
}, {
  categoryId: "drone-propulsion",
  x: 80,
  y: 55,
  label: "Material",
  spec: "7075-T6 Aerospace Aluminum"
}, {
  categoryId: "ai-infrastructure",
  x: 55,
  y: 50,
  label: "Bandwidth",
  spec: "800G Ready Interconnects"
}, {
  categoryId: "ai-infrastructure",
  x: 25,
  y: 45,
  label: "Density",
  spec: "Ultra-High Port Density Design"
}, {
  categoryId: "ai-infrastructure",
  x: 85,
  y: 65,
  label: "Latency",
  spec: "Sub-microsecond Signal Delay"
}, {
  categoryId: "fpv-systems",
  x: 50,
  y: 25,
  label: "Resolution",
  spec: "1080p 120fps Digital Link"
}, {
  categoryId: "fpv-systems",
  x: 35,
  y: 65,
  label: "Range",
  spec: "Up to 10km Long-Range Link"
}, {
  categoryId: "fpv-systems",
  x: 75,
  y: 85,
  label: "Latency",
  spec: "Fixed 28ms End-to-End Delay"
}, {
  categoryId: "flight-control",
  x: 45,
  y: 45,
  label: "Processor",
  spec: "STM32H7 High-Performance MCU"
}, {
  categoryId: "flight-control",
  x: 65,
  y: 25,
  label: "Gyro",
  spec: "Dual BMI270 Vibration-Isolated"
}, {
  categoryId: "flight-control",
  x: 55,
  y: 75,
  label: "Power",
  spec: "Integrated 12V/3A BEC System"
}];
const getIcon = (name: string) => {
  switch (name) {
    case "box":
      return <Box className="size-5 shrink-0" />;
    case "droplets":
      return <Droplets className="size-5 shrink-0" />;
    case "gamepad2":
      return <Gamepad2 className="size-5 shrink-0" />;
    case "camera":
      return <Camera className="size-5 shrink-0" />;
    case "scissors":
      return <Scissors className="size-5 shrink-0" />;
    default:
      return <Box className="size-5 shrink-0" />;
  }
};
export default function OurSmartHardwareCatalog() {
  return <section data-section-id="143295" id="product-portfolio" className="dark bg-background py-20 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <Tabs defaultValue={categories[0].id} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {}
          <div className="lg:col-span-4 flex flex-col gap-8">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-5xl font-bold tracking-tighter text-foreground">
                Advanced Product Portfolio
              </h2>
              <p className="text-muted-foreground text-lg font-sans">
                Explore our engineering-grade infrastructure and components, meticulously designed for telecom operators, drone manufacturers, and AI data centers.
              </p>
            </div>

            <TabsList className="flex flex-col h-auto w-full bg-transparent p-0 gap-3 items-stretch">
              {categories.map((cat, i) => <TabsTrigger key={cat.id} value={cat.id} data-index={i} className="group w-full justify-between px-5 py-4 h-auto text-left font-medium rounded-lg border border-border/10 bg-card/50 hover:bg-accent/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary transition-all whitespace-normal shadow-md backdrop-blur-sm">
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-md bg-muted/20 group-data-[state=active]:bg-primary-foreground/20 transition-colors">
                      {getIcon(cat.icon)}
                    </div>
                    <span className="text-base font-bold tracking-tight">{cat.title}</span>
                  </div>
                  <ChevronRight className="size-5 opacity-40 group-data-[state=active]:opacity-100 transition-opacity" />
                </TabsTrigger>)}
            </TabsList>

            <div className="pt-4">
              <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-bold tracking-wide">
                <Link to="mailto:support@galabit.com">
                  Request Technical Datasheets
                  <ChevronRight className="ml-2 size-4" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="lg:col-span-8 relative">
            {categories.map((cat, i) => <TabsContent key={cat.id} value={cat.id} data-index={i} className="relative w-full aspect-square md:aspect-[4/3] lg:aspect-[16/10] rounded-xl overflow-hidden border border-primary/10 bg-card/30 shadow-2xl mt-0 focus-visible:ring-0 backdrop-blur-md">
                {}
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#00ffff0a_1px,transparent_1px),linear-gradient(to_bottom,#00ffff0a_1px,transparent_1px)] bg-[size:32px_32px]" />
                
                <img src={cat.image} alt={cat.title} className="absolute inset-0 w-full h-full object-contain p-8 md:p-16 animate-in fade-in zoom-in-95 duration-700 opacity-90" />

                {}
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 bg-gradient-to-t from-background via-background/60 to-transparent">
                  <div className="max-w-xl">
                    <h3 className="text-2xl font-bold text-primary mb-3 tracking-tight">{cat.title}</h3>
                    <p className="text-muted-foreground text-sm md:text-base font-sans leading-relaxed">{cat.description}</p>
                  </div>
                </div>

                {}
                {hotspots.filter(h => h.categoryId === cat.id).map((spot, j) => <div key={j} data-index={j} style={{
              top: `${spot.y}%`,
              left: `${spot.x}%`
            }} className="absolute -translate-x-1/2 -translate-y-1/2 z-10">
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="icon" className="relative h-8 w-8 md:h-10 md:w-10 group rounded-full p-0 hover:bg-transparent">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-30"></span>
                              <span className="relative inline-flex size-3 md:size-4 rounded-full bg-primary border-2 border-background group-hover:scale-125 transition-transform shadow-lg"></span>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent sideOffset={12} className="p-4 max-w-[240px] bg-popover text-popover-foreground border border-primary/20 shadow-2xl backdrop-blur-xl">
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-1.5 text-primary">
                                <Plus className="size-3.5" />
                                <span className="text-[10px] uppercase tracking-widest font-mono font-bold">
                                  {spot.label}
                                </span>
                              </div>
                              <span className="text-sm font-mono font-medium leading-snug text-foreground">
                                {spot.spec}
                              </span>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>)}
              </TabsContent>)}
          </div>

        </Tabs>
      </div>
    </section>;
}